import { useCallback, useState } from 'react';
import isEmpty from 'lodash/fp/isEmpty';
import styles from './license-plates-content.module.sass';
import { useLocation, useHistory } from 'react-router-dom';
import { SearchSection } from 'componets/search-section';
import Select from 'componets/select';
import LoadingCard from 'componets/loading-card';
import { CreateLicensePlateCard } from '../create-license-plate-card';
import { CreateLicensePlateModal } from '../create-license-plate-modal';
import { LicensePlateList } from '../license-plate-list';
import { useLicensePlates } from '../use-license-plates';
import { useAppContext } from 'use-app';
import { InfoCircleFillIcon } from 'icons';
import analytics from 'service/analytics';
import { useEnterpriseTaxSettings } from 'service/use-enterprise-tax-settings';

const licenseBindStatusList = [
  {
    label: '停車綁定成功',
    value: 'bound',
  },
  {
    label: '停車綁定中',
    value: 'processing',
  },
  {
    label: '停車綁定失敗',
    value: 'binding_failed',
  },
];

const SEARCH_QUERY = 's';
const PARKING_BINDING_STATE_QUERY = 'parking_binding_state';
const TAX_ID_NUMBER_QUERY = 'tax_id_number';

export const LicensePlatesContent = () => {
  const location = useLocation();
  const history = useHistory();
  const { state } = useAppContext();
  const user = state.me;

  const isSubAccount = Boolean(user?.subAccount);

  // If the user is a sub-account, check if the sub-account has the permission to write vehicle identity
  const isUserAllowedVehicleIdentityWrite = !isSubAccount || Boolean(user?.subAccount?.vehicleIdentityWrite);

  const urlSearchParams = new URLSearchParams(location.search);
  const search = urlSearchParams.get(SEARCH_QUERY);
  const parkingBindingState = urlSearchParams.get(PARKING_BINDING_STATE_QUERY) ?? null;
  const taxIdNumber = urlSearchParams.get(TAX_ID_NUMBER_QUERY) ?? null;

  const [isCreateLicensePlateModalOpen, setIsCreateLicensePlateModalOpen] = useState(false);

  const { enterpriseTaxSettings } = useEnterpriseTaxSettings();

  const { licensePlates, isGetLicensePlatesLoading } = useLicensePlates({
    perPage: 300,
    plateNumberCont: search,
  });

  const filteredLicensePlates = filterByTaxIdNumber(
    filterByParkingBindingStatus(licensePlates, parkingBindingState),
    taxIdNumber
  );

  const parkingBindingFailedLicensePlates = licensePlates.filter(
    ({ cityParkinglots }) => cityParkinglots['taipeiCity']?.state === 'binding_failed'
  );

  const numberOfParkingBindingFailedLicensePlates = parkingBindingFailedLicensePlates.length;

  const handleCreateLicensePlateModalOpen = useCallback(() => {
    setIsCreateLicensePlateModalOpen(true);
  }, []);

  const handleCreateLicensePlateModalClose = useCallback(() => {
    setIsCreateLicensePlateModalOpen(false);
  }, []);

  const handleSearchButtonClick = useCallback(
    q => {
      let path = location.pathname;
      if (q !== '') path += `?${SEARCH_QUERY}=${q}`;
      history.push(path);
    },
    [history, location.pathname]
  );

  const handleParkingStatusSelect = useCallback(
    state => {
      const searchParams = new URLSearchParams(location.search);

      if (state == null) {
        searchParams.delete(PARKING_BINDING_STATE_QUERY);
      } else {
        searchParams.set(PARKING_BINDING_STATE_QUERY, state);

        const optionName = licenseBindStatusList.find(({ value }) => value === state)?.label;
        analytics.licensePlatesFilterParkingBindingStateSelect({ state: optionName });
      }

      history.push(`${location.pathname}?${searchParams.toString()}`);
    },
    [location, history]
  );

  const handleTaxSettingSelect = useCallback(
    taxIdNumber => {
      const searchParams = new URLSearchParams(location.search);

      if (taxIdNumber == null) {
        searchParams.delete(TAX_ID_NUMBER_QUERY);
      } else {
        searchParams.set(TAX_ID_NUMBER_QUERY, taxIdNumber);
      }

      history.push(`${location.pathname}?${searchParams.toString()}`);
    },
    [location, history]
  );

  const filterBindingFailedLicensePlates = useCallback(() => {
    analytics.licensePlatesParkingBindingFailedHintButtonClick();
    const searchParams = new URLSearchParams(location.search);
    analytics.licensePlatesParkingBindingFailedHintButtonClick();

    searchParams.set(PARKING_BINDING_STATE_QUERY, 'binding_failed');

    history.push(`${location.pathname}?${searchParams.toString()}`);
  }, [location, history]);

  // If the license plates is fetched, and the license plates are empty, show the CreateLicensePlateCard only
  if (!isGetLicensePlatesLoading && isUserAllowedVehicleIdentityWrite && isEmpty(licensePlates)) {
    return (
      <>
        <div className={styles['container']}>
          <div className={styles['container__filter-wrapper']}>
            <CreateLicensePlateCard
              maxOfLicensePlates={user?.maxVehicleCount ?? 0}
              numberOfLicensePlates={licensePlates.length}
              onClick={handleCreateLicensePlateModalOpen}
            />
          </div>
        </div>
        <CreateLicensePlateModal
          isOpen={isCreateLicensePlateModalOpen}
          enterpriseTaxSettings={enterpriseTaxSettings}
          onClose={handleCreateLicensePlateModalClose}
        />
      </>
    );
  }

  return (
    <>
      <div className={styles['container']}>
        <div className={styles['container__filter-wrapper']}>
          <SearchSection placeholder="搜尋車牌" defaultSearchQuery={search ?? ''} onSubmit={handleSearchButtonClick} />
          <div className={styles['container__filter-wrapper__selects']}>
            <Select
              value={parkingBindingState}
              placeholder="停車綁定狀態"
              data={licenseBindStatusList}
              onSelect={handleParkingStatusSelect}
              onClear={() => {
                analytics.licensePlatesFilterParkingBindingStateRemove();
                handleParkingStatusSelect(null);
              }}
            />
            <Select
              placement="bottom-right"
              value={taxIdNumber}
              placeholder="歸帳設定"
              onSelect={value => {
                analytics.licensePlatesFilterTaxIdNumberSelect({ 歸帳設定: value });
                handleTaxSettingSelect(value);
              }}
              onClear={() => {
                analytics.licensePlatesFilterTaxIdNumberRemove();
                handleTaxSettingSelect(null);
              }}
              data={enterpriseTaxSettings.map(({ taxIdNumber, title }) => ({
                label: taxIdNumber,
                subLabel: title,
                value: taxIdNumber,
              }))}
            />
          </div>
        </div>
        <div className={styles['inner-container']}>
          {parkingBindingState === 'binding_failed' && (
            <div className={styles['binding-failed-hint']}>
              ※若停車綁定失敗，車牌將無法使用台北市公有（含委外）停車場之自動扣款，請聯繫 LINE 客服協助您。
            </div>
          )}
          {isGetLicensePlatesLoading ? (
            <LoadingCard style={{ marginTop: '80px' }} />
          ) : (
            <>
              {/**
               * If there are license plates that failed to bind parking,
               * and the current filter is not 'binding_failed', show the alert hint
               */}
              {numberOfParkingBindingFailedLicensePlates > 0 && parkingBindingState !== 'binding_failed' && (
                <div
                  className={styles['inner-container__binding-failed-alert-hint']}
                  onClick={filterBindingFailedLicensePlates}
                >
                  <InfoCircleFillIcon color="#F65555" style={{ marginRight: '2px' }} />
                  查看 {numberOfParkingBindingFailedLicensePlates} 張停車綁定失敗車牌
                </div>
              )}
              <div className={styles['license-plate-card-list']}>
                {isUserAllowedVehicleIdentityWrite && urlSearchParams.size === 0 && (
                  <CreateLicensePlateCard
                    maxOfLicensePlates={user?.maxVehicleCount ?? 0}
                    numberOfLicensePlates={licensePlates.length}
                    onClick={handleCreateLicensePlateModalOpen}
                  />
                )}
                <LicensePlateList
                  enterpriseTaxSettings={enterpriseTaxSettings}
                  licensePlates={filteredLicensePlates}
                  user={user ?? null}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <CreateLicensePlateModal
        isOpen={isCreateLicensePlateModalOpen}
        enterpriseTaxSettings={enterpriseTaxSettings}
        onClose={handleCreateLicensePlateModalClose}
      />
    </>
  );
};

function filterByParkingBindingStatus(licensePlates, parkingBindingState) {
  return licensePlates.filter(({ cityParkinglots }) => {
    const taipeiParkingBindingStatus = cityParkinglots['taipeiCity']?.state;

    if (parkingBindingState == null) return true;
    return parkingBindingState === taipeiParkingBindingStatus;
  });
}

function filterByTaxIdNumber(licensePlates, selectedTaxIdNumber) {
  return licensePlates.filter(({ currentTaxIdNumber }) =>
    selectedTaxIdNumber === null ? true : currentTaxIdNumber === selectedTaxIdNumber
  );
}
